import { useTranslation } from 'react-i18next';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  CircleCheckIcon,
} from '@hero/web-icons';

/**
 * Visual category for a requirement.
 *
 * - `hard`     — must be ✓ before the application can be submitted.
 *                Incomplete renders in error tones.
 * - `soft`     — nice-to-have. Incomplete renders in warning tones
 *                and never blocks the submit.
 */
export type RequirementLevel = 'hard' | 'soft';

export interface RequirementsCardProps {
  /** Card heading (e.g. "Signed contract" / "Bank account"). */
  title: string;
  /** Single-line description shown below the title. */
  description: string;
  /** Has the requirement been satisfied? */
  complete: boolean;
  /** Hard (blocks submit) vs soft (just recommended). */
  level: RequirementLevel;
  /** CTA label shown on the right (e.g. "Add", "Complete"). */
  actionLabel: string;
  /** Fires when the user clicks the card or its CTA. */
  onAction: () => void;
  /**
   * Optional incomplete-state hint (1–2 short bullet labels). Lets
   * the card surface *exactly* which fields are missing so the user
   * doesn't have to open the drawer to find out. Hidden when the
   * card is already complete.
   */
  missingItems?: string[];
  /**
   * When true the CTA is disabled — used to wait for an upstream
   * dependency (e.g. "complete your business info before adding a
   * bank account" if we ever sequence them).
   */
  disabled?: boolean;
}

/**
 * `RequirementsCard` — Single checklist row for the financing apply
 * form's pre-submit gates.
 *
 * Renders a status icon + title + description + CTA in a card that
 * the user can click to open the matching editor (drawer). Visual
 * tone derives from `complete × level`:
 *
 * | level | complete | tone          |
 * | ----- | -------- | ------------- |
 * | hard  | true     | emerald (ok)  |
 * | hard  | false    | red (blocking)|
 * | soft  | true     | emerald (ok)  |
 * | soft  | false    | amber (warn)  |
 *
 * The whole card is clickable (treated as a button); the trailing
 * chevron + label gives a visual affordance.
 */
export function RequirementsCard({
  title,
  description,
  complete,
  level,
  actionLabel,
  onAction,
  missingItems,
  disabled,
}: RequirementsCardProps) {
  const { t } = useTranslation('m-financing');

  const tone = complete ? 'ok' : level === 'hard' ? 'block' : 'warn';
  const styles = TONE_STYLES[tone];

  return (
    <button
      type="button"
      onClick={onAction}
      disabled={disabled}
      className={[
        'group flex w-full items-start gap-3 rounded-2xl border p-4 text-start transition',
        'hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
        'disabled:cursor-not-allowed disabled:opacity-60',
        styles.surface,
      ].join(' ')}>
      <span
        className={[
          'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
          styles.iconBg,
        ].join(' ')}>
        {complete ? (
          <CircleCheckIcon className={['size-5', styles.iconColor].join(' ')} />
        ) : (
          <AlertCircleIcon className={['size-5', styles.iconColor].join(' ')} />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="truncate text-sm font-semibold text-gray-900">
            {title}
          </h4>
          {!complete && level === 'hard' ? (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
              {t('requirements.required_badge')}
            </span>
          ) : null}
          {!complete && level === 'soft' ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              {t('requirements.recommended_badge')}
            </span>
          ) : null}
          {complete ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              {t('requirements.complete_badge')}
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-600">{description}</p>
        {!complete && missingItems?.length ? (
          <ul className="mt-2 space-y-0.5">
            {missingItems.map(item => (
              <li
                key={item}
                className="flex items-center gap-1.5 text-[11px] text-gray-700">
                <span className={['size-1.5 rounded-full', styles.dotColor].join(' ')} />
                {item}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/*
       * The outer card already IS a `<button>`, so nesting a real
       * `<Button>` here would produce nested-button HTML (invalid).
       * Render the CTA affordance as plain text + icon — the whole
       * card is the clickable target.
       */}
      <span
        className={[
          'flex shrink-0 items-center gap-1 self-center text-xs font-semibold',
          styles.ctaColor,
        ].join(' ')}>
        {actionLabel}
        <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
      </span>
    </button>
  );
}

/**
 * Tone palette — keyed on the derived `tone` (`'ok' | 'block' | 'warn'`).
 * Centralised so the icon, dot, surface, and CTA colour move in sync.
 */
const TONE_STYLES = {
  ok: {
    surface: 'border-emerald-200 bg-emerald-50/40',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    dotColor: 'bg-emerald-500',
    ctaColor: 'text-emerald-700',
  },
  block: {
    surface: 'border-red-200 bg-red-50/40',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    dotColor: 'bg-red-500',
    ctaColor: 'text-red-700',
  },
  warn: {
    surface: 'border-amber-200 bg-amber-50/40',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    dotColor: 'bg-amber-500',
    ctaColor: 'text-amber-700',
  },
} as const;
